import { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { useAuth } from './useAuth';
import { ADMIN_CONFIG, hasPermission, isSuperAdmin } from '../config/admin';
import { getCurrentUserAuth, ensureSuperAdmin, AuthorizedUser } from '../utils/firestoreApi';

export interface UserRole {
  email: string;
  displayName?: string;
  role: 'super_admin' | 'admin' | 'user';
  isActive: boolean;
  permissions: string[];
}

export const useAuthWithRole = () => {
  const { user, loading: authLoading, ...authMethods } = useAuth();
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setUserRole(null);
      setLoading(false);
      return;
    }

    loadUserRole(user);
  }, [user, authLoading]);

  const loadUserRole = async (firebaseUser: User) => {
    try {
      setLoading(true);
      setError(null);

      // Ensure super admin is created
      if (firebaseUser.email && isSuperAdmin(firebaseUser.email)) {
        await ensureSuperAdmin();
      }

      // Get user authorization from Firestore
      const userData = await getCurrentUserAuth();

      if (!userData) {
        if (firebaseUser.email && isSuperAdmin(firebaseUser.email)) {
          // Auto-create super admin and retry
          await ensureSuperAdmin();
          const retryUserData = await getCurrentUserAuth();
          if (retryUserData) {
            const permissions = ADMIN_CONFIG.ROLE_PERMISSIONS[retryUserData.role] || [];
            setUserRole({
              email: retryUserData.email,
              displayName: retryUserData.displayName || firebaseUser.displayName || undefined,
              role: retryUserData.role,
              isActive: retryUserData.isActive,
              permissions
            });
            return;
          }
        }
        setError('Access denied. Contact administrator to get access.');
        setUserRole(null);
        return;
      }

      if (!userData.isActive) {
        setError('Your account has been disabled. Contact administrator.');
        setUserRole(null);
        return;
      }

      // Determine permissions based on role
      const permissions = ADMIN_CONFIG.ROLE_PERMISSIONS[userData.role] || [];

      setUserRole({
        email: userData.email,
        displayName: userData.displayName || firebaseUser.displayName || undefined,
        role: userData.role,
        isActive: userData.isActive,
        permissions
      });

    } catch (error: any) {
      console.error('Error loading user role:', error);
      setError('Error loading user permissions. Please try again.');
      setUserRole(null);
    } finally {
      setLoading(false);
    }
  };

  const hasUserPermission = (permission: string): boolean => {
    return userRole?.permissions.includes(permission) || false;
  };

  const isAdmin = (): boolean => {
    return userRole?.role === 'admin' || userRole?.role === 'super_admin' || false;
  };

  const isSuperAdminUser = (): boolean => {
    return userRole?.role === 'super_admin' || false;
  };

  return {
    firebaseUser: user,
    userRole,
    loading: loading || authLoading,
    roleError: error,
    hasPermission: hasUserPermission,
    isAdmin,
    isSuperAdmin: isSuperAdminUser,
    isAuthorized: !!user && !!userRole && userRole.isActive,
    ...authMethods
  };
};